import React, { useEffect, useState, useRef } from 'react';
import { DefaultButton, TextField, PrimaryButton, Spinner, SpinnerSize, Stack, DatePicker, Label } from '@fluentui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import useAxios from "axios-hooks";
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import { useHistory, useParams } from "react-router-dom";
import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import SelectedCobrandList from '../../../../Forms/SelectedCobrandList';
import Toast from '../../../../Forms/Toast';
import DropDown from '../../../../Forms/DropDown';
import ErrorComponent from '../../../../Util/ErrorComponent';
import { MessageBar, MessageBarType } from '@fluentui/react';
import FeatureSummary from '../../../../Util/FeatureSummary';

const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    description: Yup.string().required("Description is required"),
    cobrandTagTypeId: Yup.number().required("Tag Type is required"),
    gaCobrandTagUaNumber: Yup.string().when('cobrandTagTypeId', {
        is: 2,
        then: Yup.string().required('GA UA Number is required'),
    }),
    gaCobrandTagViewId: Yup.string().when('cobrandTagTypeId', {
        is: 2,
        then: Yup.string().required('GA View ID is required'),
    }),
    gaCobrandTagStartDate: Yup.mixed().when('cobrandTagTypeId', {
        is: 2,
        then: Yup.date()
            .typeError('GA Start Date is required') // shows only if type is wrong AND it's required
            .required('GA Start Date is required'),
        otherwise: Yup.mixed().notRequired()
    })
});

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "0 8px",
                        boxSizing: "border-box",
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

const CobrandTagsWizardPage = () => {
    const history = useHistory();
    const { cobrandTagId: cobrandTagIdFromParams } = useParams();
    const [showToast, setShowToast] = React.useState(false);
    const cobrandTagId = cobrandTagIdFromParams ? parseInt(cobrandTagIdFromParams) : 0;
    const [initialValues, setInitialValues] = useState(null);
    const [cobrandList, setCobrandList] = useState([]);
    const [showError, setShowError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [dialog, setDialog] = useState(null);
    const [gaAccounts, setGaAccounts] = React.useState([]);
    const [loadingGaAccounts, setLoadingGaAccounts] = React.useState(true);
    const [gaGenerationSuccessful, setGaGenerationSuccessful] = React.useState(false);
    const [showGaConfirmModal, setShowGaConfirmModal] = React.useState(false);
    const [ga4ValidationStatus, setGa4ValidationStatus] = React.useState('idle'); // 'idle', 'loading', 'success', 'error'
    const [ga4OverallMessage, setGa4OverallMessage] = React.useState('');
    const [ga4DetailedErrors, setGa4DetailedErrors] = React.useState([]);
    const valuesRef = React.useRef();

    const [, postNew] = useAxios({ url: "internal/cobrand/cobrand-tag/", method: "POST" }, { manual: true });
    const [, checkInTag] = useAxios({ url: "internal/cobrand/cobrand-tag-in-tag/", method: "POST" }, { manual: true });
    const [, putUpdate] = useAxios({ url: `internal/cobrand/cobrand-tag/${cobrandTagId}`, method: "PUT" }, { manual: true });
    const [{ loading: loadingTag }, getTag] = useAxios({ url: `internal/cobrand/cobrand-tag/${cobrandTagId}`, method: 'GET' }, { manual: true });
    const [{ loading: generatingGa }, generateGa] = useAxios(
        { url: `/google-analytics/generate`, method: 'POST' },
        { manual: true },
    );
    const [{ loading: validatingGa4, error: validateGa4ApiError }, executeValidateGa4] = useAxios(
        { url: `/google-analytics/validate-cobrand-tag`, method: 'GET' },
        { manual: true }
    );
    const [{ data: fetchedGaAccountsData, loading: gaAccountsLoadingState, error: gaAccountsErrorState }, fetchAccounts] = useAxios(
        { url: '/google-analytics/accounts', method: 'GET' },
        { manual: true }
    );

    const triggerGa4Validation = async (cobrandTagIdToValidate) => {
        if (!cobrandTagIdToValidate) {
            setGa4ValidationStatus('idle');
            setGa4OverallMessage('');
            setGa4DetailedErrors([]);
            return;
        }

        setGa4ValidationStatus('loading');
        setGa4OverallMessage('Validating GA4 setup...');
        setGa4DetailedErrors([]);

        try {
            const response = await executeValidateGa4({ params: { cobrandTagId: cobrandTagIdToValidate } });
            if (response && response.data && Array.isArray(response.data)) {
                const validationResultsList = response.data;
                let overallValid = true;
                const currentDetailedErrors = [];

                if (validationResultsList.length === 0) {
                    setGa4ValidationStatus('error');
                    setGa4OverallMessage('No GA4 entities found for this cobrand tag. Consider setting up Google Analytics 4 for enhanced tracking.');
                    setGa4DetailedErrors([]);
                    return;
                }

                validationResultsList.forEach(ga4Result => {
                    const levelPrefix = ga4Result.level ? `${ga4Result.level} GA4` : "GA4";
                    if (ga4Result.steps && ga4Result.steps.length > 0) {
                        ga4Result.steps.forEach(step => {
                            if (!step.passed) {
                                overallValid = false;
                                currentDetailedErrors.push(`${levelPrefix} - ${step.errorMessage || 'Failed'}`);
                            }
                        });
                    }
                });

                if (overallValid) {
                    const levelsValidatedByApi = new Set(validationResultsList.map(item => item.level).filter(Boolean));
                    if (levelsValidatedByApi.has('CobrandTag') && levelsValidatedByApi.size === 1) {
                        setGa4ValidationStatus('success');
                        setGa4OverallMessage('Cobrand Tag GA4 is valid.');
                        setGa4DetailedErrors([
                            'Google Analytics is setup for this cobrand tag',
                        ]);
                    }
                } else {
                    setGa4ValidationStatus('error');
                    setGa4OverallMessage('GA4 validation failed. See details below.');
                    setGa4DetailedErrors(currentDetailedErrors.length > 0 ? currentDetailedErrors : ['Validation failed with unspecified errors.']);
                }
            } else {
                setGa4ValidationStatus('error');
                setGa4OverallMessage('GA4 validation failed: Unexpected response from server.');
                setGa4DetailedErrors([]);
            }
        } catch (err) {
            setGa4ValidationStatus('error');
            const apiError = validateGa4ApiError || err;
            const apiErrorMsg = apiError.response?.data?.message || apiError.response?.data || apiError.message || 'An unknown error occurred.';
            setGa4OverallMessage(`GA4 validation request failed: ${apiErrorMsg}`);
            const detailedApiErrors = apiError.response?.data?.errors;
            if (Array.isArray(detailedApiErrors) && detailedApiErrors.length > 0) {
                setGa4DetailedErrors(detailedApiErrors);
            } else if (typeof apiErrorMsg === 'string' && apiErrorMsg !== 'An unknown error occurred.') {
                setGa4DetailedErrors([apiErrorMsg]);
            } else {
                setGa4DetailedErrors(['An error occurred while communicating with the server.']);
            }
            console.error("Error during GA4 validation:", apiError);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    useEffect(() => {
        setLoadingGaAccounts(gaAccountsLoadingState);

        if (gaAccountsLoadingState === false) {
            if (gaAccountsErrorState) {
                console.error("Error fetching GA accounts:", gaAccountsErrorState);
                setGaAccounts([]);
            } else if (fetchedGaAccountsData) {
                setGaAccounts(fetchedGaAccountsData.map(acc => ({ value: acc.accountId, label: acc.accountName })) || []);
            }
        }
    }, [fetchedGaAccountsData, gaAccountsLoadingState, gaAccountsErrorState, setGaAccounts, setLoadingGaAccounts]);

    useEffect(() => {
        if (cobrandTagId !== 0) {
            getTag().then(({ data }) => {
                setInitialValues({
                    ...data.cobrandTag,
                    cobrand: data.cobrandIds || [],
                });
                triggerGa4Validation(cobrandTagId);
            });

        } else {
            setInitialValues({
                name: '',
                description: '',
                cobrandTagTypeId: '',
                gaCobrandTagUaNumber: '',
                gaCobrandTagViewId: '',
                gaCobrandTagStartDate: null,
                gaCobrandTagMeasurementId: '',
                gaCobrandTagPropertyId: '',
                client: '',
                cobrandTag: '',
                cobrand: '',
                cobrandTagTypes: [],
                ga4Account: '',
            });
        }
    }, [cobrandTagId]);

    const handleSubmit = async (values, { setSubmitting }) => {
        setShowError(false)
        const isAffiliateType = values.cobrandTagTypeId === 2;
        if (isAffiliateType) {
            const updatedValues = {
                CoBrandTagId: cobrandTagId,
                Cobrands: cobrandList.map(cb => cb.coBrandId)
            };

            const response = await checkInTag({ data: updatedValues });

            if (response.data.taglist && response.data.taglist.length) {
                const currentCoBrands = response.data.taglist.map(inner =>
                    `<div>${inner.cobrandName} is already in the "Affiliate-type" tag: ${inner.tagName}</div>`
                ).join('');

                setDialog({
                    title: "Warning!",
                    message: currentCoBrands,
                    onConfirm: () => {
                        setDialog(null);
                        updateTag(values);
                    },
                    onDismiss: () => {
                        setSubmitting(false);
                        setDialog(null);
                    }
                });
                return;
            } else {
                updateTag(values, setSubmitting);
            }
        } else {
            updateTag(values, setSubmitting);
        }
    };

    const updateTag = async (values, setSubmitting) => {
        const updatedValues = {
            CobrandTag: values,
            CobrandIds: cobrandList.map(cb => cb.coBrandId)
        };

        const response = cobrandTagId === 0
            ? await postNew({ data: updatedValues })
            : await putUpdate({ data: updatedValues });

        if (!response.isAxiosError) {
            if (response.data === 2) {
                setErrorMessage("Cobrand Tag Name already taken.")
                setShowError(true)
            } else {
                setShowToast(true)
                if (cobrandTagId === 0) {
                    history.push(`/cobrand-settings-feature/cobrand-tags/${response.data}/wizard`);
                }
            }
        } else {
            setErrorMessage("An error has occured.")
            setShowError(true)
        }
    };

    if (!initialValues) return <Spinner label="Loading..." />;

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
                validateOnChange={true}
            >
                {({ values, handleChange, setFieldValue, handleBlur, setFieldTouched, validateForm, isSubmitting, errors, touched, isValid }) => {
                    valuesRef.current = values;
                    const isGenerateButtonDisabled = (
                        !isValid ||
                        !values.ga4Account ||
                        !values.name ||
                        (
                            gaGenerationSuccessful &&
                            ga4ValidationStatus === 'success' &&
                            !!values.ga4Account
                        ) ||
                        (
                            cobrandTagId > 0 &&
                            (values.gaCobrandTagPropertyId || values.gaCobrandTagMeasurementId) &&
                            ga4ValidationStatus === 'success'
                        )
                    );

                    const showGa4AccountSelect = (
                        (!values.gaCobrandTagPropertyId && !values.gaCobrandTagMeasurementId) ||
                        !cobrandTagId ||
                        ((values.gaCobrandTagPropertyId || values.gaCobrandTagMeasurementId) && ga4ValidationStatus !== 'success')
                    );

                    return (
                        <div>
                            {generatingGa && <Spinner size={SpinnerSize.large} label="Generating GA Data..." />}
                            {gaGenerationSuccessful.message && (
                                <MessageBar messageBarType={gaGenerationSuccessful.type} isMultiline={false} onDismiss={() => setGaGenerationSuccessful({ message: '', type: null })} dismissButtonAriaLabel="Close" style={{ marginTop: '15px' }}>
                                    {gaGenerationSuccessful.message}
                                </MessageBar>
                            )}

                            {(ga4ValidationStatus !== 'idle' || validatingGa4) && (
                                <div style={{ margin: '15px 0', paddingLeft: '15px', paddingRight: '15px' }}>
                                    {(ga4ValidationStatus === 'loading' || (validatingGa4 && ga4ValidationStatus === 'idle')) &&
                                        <Spinner label={ga4OverallMessage || "Validating GA4 setup..."} />
                                    }
                                    {ga4ValidationStatus === 'success' && (
                                        <MessageBar
                                            messageBarType={MessageBarType.success}
                                            isMultiline={false}
                                            onDismiss={() => {
                                                setGa4ValidationStatus('idle');
                                                setGa4OverallMessage('');
                                                setGa4DetailedErrors([]);
                                            }}
                                            dismissButtonAriaLabel="Close"
                                        >
                                            {ga4OverallMessage}
                                        </MessageBar>
                                    )}
                                    {ga4ValidationStatus === 'warning' && (
                                        <MessageBar
                                            messageBarType={MessageBarType.warning}
                                            isMultiline={true}
                                            onDismiss={() => {
                                                setGa4ValidationStatus('idle');
                                                setGa4OverallMessage('');
                                                setGa4DetailedErrors([]);
                                            }}
                                            dismissButtonAriaLabel="Close"
                                        >
                                            {ga4OverallMessage}
                                            {ga4DetailedErrors && ga4DetailedErrors.length > 0 && (
                                                <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px', textAlign: 'left' }}>
                                                    {ga4DetailedErrors.map((err, idx) => <li key={idx}>{err}</li>)}
                                                </ul>
                                            )}
                                        </MessageBar>
                                    )}
                                    {ga4ValidationStatus === 'error' && (
                                        <MessageBar
                                            messageBarType={MessageBarType.error}
                                            isMultiline={true}
                                            onDismiss={() => {
                                                setGa4ValidationStatus('idle');
                                                setGa4OverallMessage('');
                                                setGa4DetailedErrors([]);
                                            }}
                                            dismissButtonAriaLabel="Close"
                                        >
                                            {ga4OverallMessage}
                                            {ga4DetailedErrors && ga4DetailedErrors.length > 0 && (
                                                <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px', textAlign: 'left' }}>
                                                    {ga4DetailedErrors.map((err, idx) => <li key={idx}>{err}</li>)}
                                                </ul>
                                            )}
                                        </MessageBar>
                                    )}
                                </div>
                            )}

                            <Form>
                                {!loadingTag && <Stack tokens={{ childrenGap: 5 }} style={{ width: 500 }}>

                                    <ErrorComponent
                                        errorMessage={errorMessage}
                                        showError={showError}
                                        setShowError={setShowError}
                                    />
                                    <TextField
                                        label="Cobrand Tag Name"
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        errorMessage={touched.name && errors.name}
                                        required
                                    />

                                    <TextField
                                        label="Cobrand Tag Description"
                                        name="description"
                                        multiline
                                        rows={3}
                                        value={values.description}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        errorMessage={touched.description && errors.description}
                                        required
                                    />

                                    <DropDown
                                        label="Cobrand Tag Type"
                                        placeholder="Select an option"
                                        options={[
                                            { key: 1, text: "Standard" },
                                            { key: 2, text: "Affiliate" },
                                            { key: 3, text: "Brand" }
                                        ]}
                                        name="cobrandTagTypeId"
                                        required
                                    />
                                    {cobrandTagId > 0 && (
                                        <>
                                            <TextField
                                                label="Cobrand Tag GA UA Number"
                                                name="gaCobrandTagUaNumber"
                                                value={values.gaCobrandTagUaNumber}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                placeholder="UA-XXXXXXXX-XX"
                                                errorMessage={touched.gaCobrandTagUaNumber && errors.gaCobrandTagUaNumber}
                                            />

                                            <TextField
                                                label="Cobrand Tag GA View ID"
                                                name="gaCobrandTagViewId"
                                                value={values.gaCobrandTagViewId}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                errorMessage={touched.gaCobrandTagViewId && errors.gaCobrandTagViewId}
                                            />
                                        </>
                                    )}

                                    <div>
                                        <DatePicker
                                            label="Cobrand Tag GA Start Date"
                                            value={values.gaCobrandTagStartDate ? new Date(values.gaCobrandTagStartDate) : null}
                                            onSelectDate={(date) => {
                                                setFieldTouched("gaCobrandTagStartDate", true);
                                                setFieldValue("gaCobrandTagStartDate", date)
                                            }}
                                            placeholder="Select a date"
                                            isRequired={values.cobrandTagTypeId === 2}
                                            ariaLabel="GA Start Date"
                                            errorMessage={touched.gaCobrandTagStartDate && errors.gaCobrandTagStartDate}
                                            strings={{
                                                months: [...Array(12)].map((_, i) => new Date(0, i).toLocaleString('default', { month: 'long' })),
                                                shortMonths: [...Array(12)].map((_, i) => new Date(0, i).toLocaleString('default', { month: 'short' })),
                                                days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                                                shortDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
                                                goToToday: 'Go to today'
                                            }}
                                        />

                                        <TextField
                                            label="Cobrand Tag Measurement ID"
                                            name="gaCobrandTagMeasurementId"
                                            value={values.gaCobrandTagMeasurementId}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            placeholder="G-XXXXXXXXXX"
                                            required={values.cobrandTagTypeId === 2}
                                            disabled
                                            errorMessage={touched.gaCobrandTagMeasurementId && errors.gaCobrandTagMeasurementId}
                                        />

                                        <TextField
                                            label="Cobrand Tag GA Property ID"
                                            name="gaCobrandTagPropertyId"
                                            value={values.gaCobrandTagPropertyId}
                                            onBlur={handleBlur}
                                            required={values.cobrandTagTypeId === 2}
                                            onChange={handleChange}
                                            disabled
                                            errorMessage={touched.gaCobrandTagPropertyId && errors.gaCobrandTagPropertyId}
                                        />
                                    </div>
                                    {showGa4AccountSelect && (
                                        <div>
                                            <Label required styles={{ root: { fontSize: '14px', fontWeight: 600, color: 'rgb(50, 49, 48)' } }}>GA4 Account</Label>
                                            <Select
                                                components={{ MenuList: CustomMenuList }}
                                                options={gaAccounts}
                                                placeholder={loadingGaAccounts ? "Loading accounts..." : "Select an account"}
                                                name="ga4Account"
                                                value={gaAccounts?.find(
                                                    (option) => option.value === values.ga4Account
                                                )}
                                                onChange={(option) => {
                                                    setFieldValue('ga4Account', option ? option.value : null);
                                                }}
                                                isSearchable
                                                isClearable
                                                isDisabled={loadingGaAccounts}
                                                styles={{ container: base => ({ ...base, marginBottom: '10px', width: 400 }), menuList: (base) => ({ ...base, maxHeight: "300px", overflowY: "auto" }) }}
                                            />
                                            {touched.ga4Account && errors.ga4Account && (
                                                <div style={{ color: 'rgb(164, 38, 44)', fontSize: '12px', marginTop: '4px' }}>
                                                    {errors.ga4Account}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', flexDirection: 'column', alignSelf: 'flex-start', marginTop: '20px', gap: '10px' }}>
                                        <PrimaryButton
                                            iconProps={{ iconName: 'Refresh' }}
                                            text="Generate GA Data"
                                            disabled={isGenerateButtonDisabled}
                                            onClick={() => setShowGaConfirmModal(true)}
                                        />

                                        <Dialog
                                            hidden={!showGaConfirmModal}
                                            onDismiss={() => setShowGaConfirmModal(false)}
                                            dialogContentProps={{
                                                type: DialogType.normal,
                                                title: 'Confirm Google Analytics Asset Generation',
                                                subText: `Generating new Google Analytics properties and web data streams for the cobrand tag: ${values.name} will overwrite any existing Google Analytics values. Are you sure you want to proceed?`
                                            }}
                                            modalProps={{
                                                isBlocking: true,
                                                styles: { main: { maxWidth: 450 } }
                                            }}
                                        >
                                            <DialogFooter>
                                                <PrimaryButton
                                                    onClick={() => {
                                                        setShowGaConfirmModal(false);
                                                        const payload = {
                                                            cobrandTagId: parseInt(cobrandTagId || 0),
                                                            isEnrich: 2,
                                                            GA4Account: values.ga4Account,
                                                            levels: ["CobrandTag"],
                                                            cobrandTagDisplayName: values.name,
                                                        };

                                                        setGaGenerationSuccessful(false);
                                                        generateGa({ data: payload })
                                                            .then(response => {
                                                                const responseData = response.data;
                                                                if (Array.isArray(responseData) && responseData.length > 0) {
                                                                    responseData.forEach(item => {
                                                                        const propertyId = item.gaProperty?.propertyName?.propertyId;
                                                                        const measurementId = item.gaDataStream?.webStreamData?.measurementId;
                                                                        setFieldValue('gaCobrandTagPropertyId', propertyId);
                                                                        setFieldValue('gaCobrandTagMeasurementId', measurementId);
                                                                    });
                                                                    setGaGenerationSuccessful(true);
                                                                    triggerGa4Validation(cobrandTagId);
                                                                } else {
                                                                    console.warn("GA data response was not in the expected format or was empty:", responseData);
                                                                }
                                                                setFieldValue('ga4Account', null);
                                                            })
                                                            .catch(error => {
                                                                console.error("Error generating GA data:", error);
                                                            });
                                                    }}
                                                    text="Generate"
                                                />
                                                <DefaultButton onClick={() => setShowGaConfirmModal(false)} text="Cancel" />
                                            </DialogFooter>
                                        </Dialog>
                                    </div>

                                    <SelectedCobrandList
                                        clients={values.client}
                                        tags={values.cobrandTag}
                                        cobrands={values.cobrand}
                                        setParentCobrandList={setCobrandList}
                                    />

                                    <PrimaryButton type="submit" style={{ marginTop: 20 }} text={isSubmitting ? "Saving..." : "Submit"} disabled={isSubmitting} />
                                    <Toast show={showToast} message="Saved!" onClose={() => setShowToast(false)} />
                                </Stack>}
                            </Form>
                        </div>
                    );
                }}
            </Formik>

            <FeatureSummary
                label="About Cobrand Tags"
                summary="Create or edit cobrand tags to group cobrands for reporting. The GA fields automate creating GA properties to prevent errors. 'GA UA Number' and 'GA View ID' are deprecated—use '00000' if no historical value exists. To enable GA tracking: set the GA Start Date, select a GA account (multiple accounts exist due to legacy property caps), click 'Generate GA Data', then Submit. Refresh to validate—if successful, the tag will start collecting GA data."
            />

            {dialog && (
                <Dialog
                    hidden={false}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: dialog.title,
                    }}
                    onDismiss={dialog.onDismiss}
                    modalProps={{
                        styles: {
                            main: {
                                width: '500px !important',
                                maxWidth: '500px !important',
                                minWidth: '500px !important',
                            }
                        }
                    }}
                >
                    <div>Cobrands can be in as many "Standard-type" tags as you like, but they may only be in 1 "Affiliate-type" tag for Google Analytics related reasons.</div>
                    <div className='mb20'>Press OK to move this cobrand(s) to this tag and remove it from their current Affiliate tag.</div>
                    <div dangerouslySetInnerHTML={{ __html: dialog.message }} />
                    <div className='mt20'>Press OK to move this cobrand(s) to this tag and remove it from their current Affiliate tag.</div>
                    <DialogFooter>
                        <PrimaryButton onClick={dialog.onConfirm} text="OK" />
                        <PrimaryButton onClick={dialog.onDismiss} text="Cancel" />
                    </DialogFooter>
                </Dialog>
            )
            }
        </>
    );
};

export default CobrandTagsWizardPage;
